import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { client } from "@/data/client";
import { cn } from "@/lib/utils";
import type { ConversationDayLead } from "@/data/types";

type SortKey = "recent" | "messages" | "slowest";
type StatusFilter = "all" | "new" | "returning";

export function ConversationDaySection({
  date,
  onClear,
}: {
  date: string | null;
  onClear: () => void;
}) {
  const [sortBy, setSortBy] = useState<SortKey>("recent");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 7;

  const q = useQuery({
    queryKey: ["conversation-day", date],
    queryFn: () => client.getConversationDayDetail({ date: date! }),
    enabled: !!date,
  });

  // Reset filtros + página quando trocar de dia.
  useEffect(() => {
    setSortBy("recent");
    setStatusFilter("all");
    setSearch("");
    setPage(1);
  }, [date]);

  // Reset página quando mudar qualquer filtro.
  useEffect(() => {
    setPage(1);
  }, [sortBy, statusFilter, search]);

  const detail = q.data;

  const visible = useMemo(() => {
    if (!detail) return [];
    const filtered = detail.leads.filter((l) => {
      if (statusFilter === "new" && !l.isNewContact) return false;
      if (statusFilter === "returning" && l.isNewContact) return false;
      if (search && !l.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
    return [...filtered].sort((a, b) => {
      if (sortBy === "messages") return b.messageCount - a.messageCount;
      if (sortBy === "slowest") return b.avgResponseMinutes - a.avgResponseMinutes;
      return b.lastMessageAt.localeCompare(a.lastMessageAt);
    });
  }, [detail, sortBy, statusFilter, search]);

  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const pageItems = useMemo(
    () => visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [visible, page]
  );

  // Placeholder quando nenhum dia foi selecionado.
  if (!date) {
    return (
      <section className="rounded-lg border border-dashed border-border-subtle bg-bg-card/50 p-8 text-center">
        <div className="mx-auto max-w-md space-y-1.5">
          <div className="text-sm font-medium text-text-secondary">Conversas do período</div>
          <p className="text-[13px] text-text-muted">
            Clique em qualquer dia do gráfico acima para ver a listagem completa de leads que interagiram nesse dia —
            nome, quantidade de mensagens e tempo médio de resposta.
          </p>
        </div>
      </section>
    );
  }

  const dateLabel = new Date(date).toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  return (
    <section className="overflow-hidden rounded-lg border border-border-subtle bg-bg-card">
      {/* Header */}
      <header className="flex items-start justify-between gap-4 border-b border-border-subtle px-6 py-5">
        <div className="space-y-1">
          <div className="text-[11px] font-medium uppercase tracking-[0.15em] text-brand-amber">
            Conversas do período
          </div>
          <h2 className="text-lg font-semibold capitalize tracking-tight text-text-primary">{dateLabel}</h2>
          {detail && (
            <p className="text-[13px] text-text-muted">
              <span className="font-semibold text-text-primary">{detail.totalLeads}</span> leads interagiram ·
              <span className="ml-1 font-semibold text-text-primary">{detail.totalMessages}</span> mensagens ·
              <span className="ml-1 font-semibold text-brand-amber">{detail.newContacts}</span> novos contatos ·
              <span className="ml-1 font-semibold text-success">{detail.answered}</span> respondidas
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onClear}
          className="flex items-center gap-1.5 rounded-md border border-border-subtle px-3 py-1.5 text-xs font-medium text-text-muted transition hover:border-border-strong hover:text-text-primary"
        >
          <span>×</span>
          <span>Limpar dia</span>
        </button>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 border-b border-border-subtle px-6 py-3">
        <input
          type="search"
          placeholder="Buscar por nome do lead…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-9 flex-1 min-w-[220px] rounded-md border border-border-subtle bg-bg-primary px-3 text-sm text-text-primary placeholder:text-text-muted focus:border-brand-amber focus:outline-none"
        />
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <span>Status:</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="h-9 rounded-md border border-border-subtle bg-bg-primary px-2 text-sm text-text-primary focus:border-brand-amber focus:outline-none"
          >
            <option value="all">Todos</option>
            <option value="new">Novos contatos</option>
            <option value="returning">Recorrentes</option>
          </select>
        </div>
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <span>Ordenar:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortKey)}
            className="h-9 rounded-md border border-border-subtle bg-bg-primary px-2 text-sm text-text-primary focus:border-brand-amber focus:outline-none"
          >
            <option value="recent">Mais recentes</option>
            <option value="messages">Mais mensagens</option>
            <option value="slowest">TMR mais lento</option>
          </select>
        </div>
        <div className="ml-auto text-xs text-text-muted">
          {visible.length} de {detail?.totalLeads ?? 0}
        </div>
      </div>

      {/* Table */}
      <div>
        {q.isLoading && (
          <div className="px-6 py-12 text-center text-sm text-text-muted">Carregando interações…</div>
        )}

        {detail && visible.length === 0 && !q.isLoading && (
          <div className="px-6 py-12 text-center text-sm text-text-muted">
            Nenhuma conversa encontrada com os filtros atuais.
          </div>
        )}

        {visible.length > 0 && (
          <>
          <table className="w-full">
            <thead className="bg-bg-secondary">
              <tr className="text-[11px] font-medium uppercase tracking-[0.1em] text-text-muted">
                <th className="px-5 py-3 text-left">Contato</th>
                <th className="px-5 py-3 text-right">Mensagens</th>
                <th className="px-5 py-3 text-right">TMR (min)</th>
                <th className="px-5 py-3 text-left">Última msg</th>
                <th className="px-5 py-3 text-left">Atendido por</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((lead, i) => (
                <LeadRow key={lead.contactId} lead={lead} isLast={i === pageItems.length - 1} />
              ))}
            </tbody>
          </table>
          {totalPages > 1 && (
            <Paginator
              page={page}
              totalPages={totalPages}
              onChange={setPage}
              rangeStart={(page - 1) * PAGE_SIZE + 1}
              rangeEnd={Math.min(page * PAGE_SIZE, visible.length)}
              total={visible.length}
            />
          )}
          </>
        )}
      </div>
    </section>
  );
}

function Paginator({
  page,
  totalPages,
  onChange,
  rangeStart,
  rangeEnd,
  total,
}: {
  page: number;
  totalPages: number;
  onChange: (p: number) => void;
  rangeStart: number;
  rangeEnd: number;
  total: number;
}) {
  return (
    <div className="flex items-center justify-between border-t border-border-subtle px-6 py-3">
      <span className="text-xs text-text-muted">
        Exibindo <span className="font-semibold text-text-primary">{rangeStart}–{rangeEnd}</span> de {total}
      </span>
      <div className="flex items-center gap-1">
        <PagerButton label="‹" disabled={page === 1} onClick={() => onChange(page - 1)} />
        {buildPageWindow(page, totalPages).map((p, i) =>
          p === "…" ? (
            <span key={`ellipsis-${i}`} className="px-2 text-xs text-text-muted">…</span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onChange(p)}
              className={cn(
                "min-w-[30px] rounded-md px-2 py-1 text-xs font-medium transition",
                p === page
                  ? "bg-brand-amber text-text-on-accent"
                  : "text-text-secondary hover:bg-bg-card-hover hover:text-text-primary"
              )}
            >
              {p}
            </button>
          )
        )}
        <PagerButton label="›" disabled={page === totalPages} onClick={() => onChange(page + 1)} />
      </div>
    </div>
  );
}

function PagerButton({ label, disabled, onClick }: { label: string; disabled: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex h-7 w-7 items-center justify-center rounded-md text-sm transition",
        disabled
          ? "cursor-not-allowed text-text-muted/40"
          : "text-text-secondary hover:bg-bg-card-hover hover:text-text-primary"
      )}
    >
      {label}
    </button>
  );
}

/**
 * Janela de paginação estilo Google: [1] 2 3 … 12. Mostra no máximo 5 páginas
 * visíveis + "…" como separador quando o total passa disso.
 */
function buildPageWindow(current: number, total: number): (number | "…")[] {
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | "…")[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push("…");
  for (let p = start; p <= end; p++) pages.push(p);
  if (end < total - 1) pages.push("…");
  pages.push(total);
  return pages;
}

function LeadRow({ lead, isLast }: { lead: ConversationDayLead; isLast: boolean }) {
  const phoneFmt = lead.number.startsWith("55")
    ? `+55 (${lead.number.slice(2, 4)}) ${lead.number.slice(4, 9)}-${lead.number.slice(9)}`
    : lead.number;
  const lastMsgTime = new Date(lead.lastMessageAt).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const tmrSeverity =
    lead.avgResponseMinutes >= 20 ? "danger" : lead.avgResponseMinutes >= 10 ? "warning" : "ok";
  const tmrChip = {
    danger: "bg-danger/15 text-danger",
    warning: "bg-warning/15 text-warning",
    ok: "bg-success/15 text-success",
  }[tmrSeverity];

  return (
    <tr
      className={cn(
        "text-sm transition hover:bg-bg-card-hover",
        !isLast && "border-b border-border-subtle"
      )}
    >
      <td className="px-5 py-3">
        <div className="flex items-center gap-3">
          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold text-text-on-accent"
            style={{ backgroundColor: lead.avatarColor }}
          >
            {getInitials(lead.name)}
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-text-primary">{lead.name}</span>
              {lead.isNewContact && (
                <span className="inline-flex rounded-full bg-brand-amber/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand-amber">
                  Novo
                </span>
              )}
            </div>
            <div className="text-xs text-text-muted">{phoneFmt}</div>
            {lead.lastMessagePreview && (
              <div className="mt-1 truncate text-xs italic text-text-muted">"{lead.lastMessagePreview}"</div>
            )}
          </div>
        </div>
      </td>
      <td className="px-5 py-3 text-right">
        <div className="font-semibold text-text-primary">{lead.messageCount}</div>
        <div className="text-[11px] text-text-muted">{lead.inboundMessages} recebidas</div>
      </td>
      <td className="px-5 py-3 text-right">
        <span className={cn("inline-flex rounded-full px-2 py-0.5 text-xs font-semibold", tmrChip)}>
          {lead.avgResponseMinutes.toFixed(1).replace(".", ",")} min
        </span>
      </td>
      <td className="px-5 py-3 text-text-secondary">{lastMsgTime}</td>
      <td className="px-5 py-3 text-text-secondary">{lead.assignedUserName ?? "—"}</td>
    </tr>
  );
}

function getInitials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
